import {
	AdminNewsletterSubscriberStatusEnum,
	OrderTypes,
	useDeleteManyAdminNewsletterSubscriberMutation,
	useGetAllAdminNewsletterSubscriberQuery,
	useUploadAdminNewsletterSubscribersMutation,
} from '@codezo/graphql';
import { DataContainer, formatDateForTable } from '@codezo/ui';
import {
	Alert,
	Anchor,
	Badge,
	Button,
	Checkbox,
	Group,
	Modal,
	Pagination,
	Paper,
	Select,
	Stack,
	Table,
	Text,
	TextInput,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
	IconChevronDown,
	IconChevronUp,
	IconMail,
	IconSearch,
	IconTrash,
	IconUpload,
	IconUsers,
} from '@tabler/icons-react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { SendEmailModal } from './SendEmailModal';

const sortableHeaderStyles = `
	.sortable-header .sort-icon-wrapper {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 20px;
		margin-left: 4px;
		vertical-align: middle;
	}
	.sortable-header .sort-icon-hover {
		opacity: 0;
		transition: opacity 0.15s ease;
		display: inline-flex;
		align-items: center;
	}
	.sortable-header:hover .sort-icon-hover {
		opacity: 1;
	}
`;

type StatusFilterValue = 'ALL' | AdminNewsletterSubscriberStatusEnum;

const STATUS_FILTER_OPTIONS = [
	{ value: 'ALL', label: 'All statuses' },
	{ value: AdminNewsletterSubscriberStatusEnum.Active, label: 'Active' },
	{ value: AdminNewsletterSubscriberStatusEnum.Unsubscribed, label: 'Unsubscribed' },
];

const statusBadgeColor = (status: AdminNewsletterSubscriberStatusEnum): string => {
	if (status === AdminNewsletterSubscriberStatusEnum.Active) return 'green';
	return 'orange';
};

interface CsvRow {
	email: string;
	firstName?: string;
	lastName?: string;
	createdAt?: string;
}

function parseCsv(text: string): CsvRow[] {
	const lines = text.split(/\r?\n/).filter((line) => line.trim());
	if (lines.length < 2) return [];

	const headerLine = lines[0].toLowerCase();
	const headers = headerLine.split(',').map((h) => h.trim());

	const emailIdx = headers.findIndex(
		(h) => h === 'email' || h === 'e-mail' || h === 'email address'
	);
	const firstNameIdx = headers.findIndex(
		(h) =>
			h === 'firstname' ||
			h === 'first_name' ||
			h === 'first name' ||
			h === 'fornavn' ||
			h === 'first'
	);
	const lastNameIdx = headers.findIndex(
		(h) =>
			h === 'lastname' ||
			h === 'last_name' ||
			h === 'last name' ||
			h === 'efternavn' ||
			h === 'last'
	);
	const createdAtIdx = headers.findIndex(
		(h) =>
			h === 'createdat' ||
			h === 'created_at' ||
			h === 'created at' ||
			h === 'created' ||
			h === 'oprettet' ||
			h === 'date'
	);

	if (emailIdx === -1) return [];

	const rows: CsvRow[] = [];
	for (let i = 1; i < lines.length; i++) {
		const cols = lines[i].split(',').map((c) => c.trim());
		const email = cols[emailIdx];
		if (!email) continue;

		const createdAtRaw = createdAtIdx >= 0 ? cols[createdAtIdx] : undefined;
		const createdAt = createdAtRaw && !isNaN(Date.parse(createdAtRaw)) ? createdAtRaw : undefined;

		rows.push({
			email,
			firstName: firstNameIdx >= 0 ? cols[firstNameIdx] || undefined : undefined,
			lastName: lastNameIdx >= 0 ? cols[lastNameIdx] || undefined : undefined,
			createdAt,
		});
	}
	return rows;
}

export function NewsletterSubscribersTable() {
	const [searchValue, setSearchValue] = useState('');
	const [debouncedSearch] = useDebouncedValue(searchValue, 300);
	const [statusFilter, setStatusFilter] = useState<StatusFilterValue>(
		AdminNewsletterSubscriberStatusEnum.Active
	);
	const [selectedIds, setSelectedIds] = useState<string[]>([]);
	const [sortField, setSortField] = useState<string>('createdAt');
	const [sortDirection, setSortDirection] = useState<OrderTypes>(OrderTypes.Desc);
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize] = useState(10);
	const [sendModalOpen, setSendModalOpen] = useState(false);
	const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
	const [selectAllMatching, setSelectAllMatching] = useState(false);
	const fileInputRef = useRef<HTMLInputElement>(null);

	const { data, loading, error, refetch } = useGetAllAdminNewsletterSubscriberQuery({
		variables: {
			input: {
				pagination: { currentPage, limit: pageSize },
				search: debouncedSearch || undefined,
				order: { field: sortField, direction: sortDirection },
				status:
					statusFilter === 'ALL'
						? undefined
						: (statusFilter as AdminNewsletterSubscriberStatusEnum),
			},
		},
		errorPolicy: 'all',
		fetchPolicy: 'network-only',
	});

	const [uploadSubscribers, { loading: uploading }] = useUploadAdminNewsletterSubscribersMutation({
		onCompleted: (result) => {
			const summary = result.uploadAdminNewsletterSubscribers;
			notifications.show({
				title: 'Upload Complete',
				message: `Created ${summary.created} • Updated ${summary.updated} • Skipped ${summary.skipped}`,
				color: summary.success ? 'green' : 'orange',
			});
			refetch();
		},
		onError: (err) => {
			notifications.show({
				title: 'Upload Failed',
				message: err.message || 'Failed to upload subscribers',
				color: 'red',
			});
		},
	});

	const [deleteSubscribers, { loading: deleting }] = useDeleteManyAdminNewsletterSubscriberMutation({
		onCompleted: (result) => {
			const summary = result.deleteManyAdminNewsletterSubscriber;
			notifications.show({
				title: 'Subscribers Deleted',
				message: summary.message || `Deleted ${summary.deleted} subscribers`,
				color: 'green',
			});
			setSelectedIds([]);
			setSelectAllMatching(false);
			setDeleteConfirmOpen(false);
			refetch();
		},
		onError: (err) => {
			notifications.show({
				title: 'Delete Failed',
				message: err.message || 'Failed to delete subscribers',
				color: 'red',
			});
		},
	});

	const handleDelete = () => {
		deleteSubscribers({
			variables: {
				input: { ids: selectedIds },
			},
		});
	};

	const handleFileSelect = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const file = event.target.files?.[0];
			if (!file) return;

			const reader = new FileReader();
			reader.onload = (e) => {
				const text = e.target?.result as string;
				const rows = parseCsv(text);

				if (rows.length === 0) {
					notifications.show({
						title: 'Invalid CSV',
						message:
							'Could not parse the CSV file. Ensure it has an "email" column header.',
						color: 'red',
					});
					return;
				}

				notifications.show({
					title: 'Uploading',
					message: `Uploading ${rows.length} subscribers...`,
					color: 'gray',
				});

				uploadSubscribers({
					variables: {
						input: rows.map((r) => ({
							email: r.email,
							firstName: r.firstName || null,
							lastName: r.lastName || null,
							createdAt: r.createdAt || null,
						})),
					},
				});
			};
			reader.readAsText(file);

			event.target.value = '';
		},
		[uploadSubscribers]
	);

	const subscribers = useMemo(() => {
		return data?.getAllAdminNewsletterSubscriber?.data || [];
	}, [data?.getAllAdminNewsletterSubscriber?.data]);

	const totalCount = data?.getAllAdminNewsletterSubscriber?.pageInfo?.totalCount ?? 0;

	const selectAll = () => {
		if (selectedIds.length === subscribers.length && subscribers.length > 0) {
			setSelectedIds([]);
			setSelectAllMatching(false);
		} else {
			setSelectedIds(subscribers.map((s) => s.id));
			setSelectAllMatching(false);
		}
	};

	const isAllSelected = selectedIds.length === subscribers.length && subscribers.length > 0;
	const isIndeterminate = selectedIds.length > 0 && selectedIds.length < subscribers.length;

	const handleSort = (field: string) => {
		if (sortField === field) {
			setSortDirection(sortDirection === OrderTypes.Asc ? OrderTypes.Desc : OrderTypes.Asc);
		} else {
			setSortField(field);
			setSortDirection(OrderTypes.Asc);
		}
		setCurrentPage(1);
	};

	const handlePageChange = (page: number) => {
		setCurrentPage(page);
		setSelectedIds([]);
		setSelectAllMatching(false);
	};

	const getSortIcon = (field: string) => {
		if (sortField !== field) {
			return (
				<span className="sort-icon-wrapper">
					<span className="sort-icon-hover">
						<IconChevronDown size={14} />
					</span>
				</span>
			);
		}
		return (
			<span className="sort-icon-wrapper">
				<span
					style={{
						background: 'var(--mantine-color-gray-1)',
						borderRadius: '4px',
						padding: '2px',
						display: 'inline-flex',
						alignItems: 'center',
					}}
				>
					{sortDirection === OrderTypes.Asc ? (
						<IconChevronUp size={14} />
					) : (
						<IconChevronDown size={14} />
					)}
				</span>
			</span>
		);
	};

	return (
		<>
			<style>{sortableHeaderStyles}</style>
			<input
				type="file"
				ref={fileInputRef}
				accept=".csv"
				style={{ display: 'none' }}
				onChange={handleFileSelect}
			/>
			<Stack>
				<Group justify="space-between" align="flex-start">
					<div>
						<Text size="lg" fw={600}>
							Newsletter Subscribers
						</Text>
						<Text size="sm" c="dimmed">
							Upload subscribers via CSV and send templated emails
						</Text>
					</div>
					<Group>
						<Select
							data={STATUS_FILTER_OPTIONS}
							value={statusFilter}
							onChange={(value) => {
								if (!value) return;
								setStatusFilter(value as StatusFilterValue);
								setCurrentPage(1);
								setSelectedIds([]);
								setSelectAllMatching(false);
							}}
							w={170}
							allowDeselect={false}
						/>
						<TextInput
							placeholder="Search subscribers..."
							leftSection={<IconSearch size={16} />}
							value={searchValue}
							onChange={(e) => {
								setSearchValue(e.currentTarget.value);
								setCurrentPage(1);
							}}
							w={250}
						/>
						{(selectedIds.length > 0 || selectAllMatching) && (
							<>
								<Button
									color="dark"
									leftSection={<IconMail size={16} />}
									onClick={() => setSendModalOpen(true)}
								>
									Send email to{' '}
									{selectAllMatching
										? `all ${totalCount} matching`
										: `${selectedIds.length} selected`}
								</Button>
								{!selectAllMatching && (
									<Button
										variant="default"
										color="red"
										leftSection={<IconTrash size={16} />}
										onClick={() => setDeleteConfirmOpen(true)}
									>
										Delete {selectedIds.length}
									</Button>
								)}
							</>
						)}
						<Button
							variant="default"
							leftSection={<IconUpload size={16} />}
							loading={uploading}
							onClick={() => fileInputRef.current?.click()}
						>
							Upload CSV
						</Button>
					</Group>
				</Group>

				<DataContainer loading={loading} error={error}>
					{isAllSelected && totalCount > subscribers.length && !selectAllMatching && (
						<Alert variant="light" color="gray" py="xs" mb="sm">
							<Text size="sm" ta="center">
								All {subscribers.length} subscribers on this page are selected.{' '}
								<Anchor
									size="sm"
									component="button"
									onClick={() => setSelectAllMatching(true)}
								>
									Select all {totalCount} matching subscribers
								</Anchor>
							</Text>
						</Alert>
					)}
					{selectAllMatching && (
						<Alert variant="light" color="gray" py="xs" mb="sm">
							<Text size="sm" ta="center">
								All {totalCount} matching subscribers are selected.{' '}
								<Anchor
									size="sm"
									component="button"
									onClick={() => {
										setSelectAllMatching(false);
										setSelectedIds([]);
									}}
								>
									Clear selection
								</Anchor>
							</Text>
						</Alert>
					)}
					<Paper p="lg" withBorder style={{ overflow: 'hidden' }}>
						<Table.ScrollContainer minWidth={700}>
							<Table verticalSpacing="sm" highlightOnHover>
								<Table.Thead>
									<Table.Tr>
										<Table.Th w={40}>
											<Checkbox
												checked={isAllSelected}
												indeterminate={isIndeterminate}
												onChange={selectAll}
											/>
										</Table.Th>
										<Table.Th>Email</Table.Th>
										<Table.Th
											className="sortable-header"
											style={{ cursor: 'pointer' }}
											onClick={() => handleSort('firstName')}
										>
											<Group gap={4} wrap="nowrap">
												Name
												{getSortIcon('firstName')}
											</Group>
										</Table.Th>
										<Table.Th>Status</Table.Th>
										<Table.Th
											className="sortable-header"
											style={{ cursor: 'pointer' }}
											onClick={() => handleSort('createdAt')}
										>
											<Group gap={4} wrap="nowrap">
												Added
												{getSortIcon('createdAt')}
											</Group>
										</Table.Th>
									</Table.Tr>
								</Table.Thead>
								<Table.Tbody>
									{subscribers.length === 0 ? (
										<Table.Tr>
											<Table.Td colSpan={5}>
												<Stack align="center" gap="xl" py={60}>
													<IconUsers size={80} stroke={0.8} />
													<Stack align="center" gap="sm">
														<Text size="lg" fw={600} c="gray.7">
															No subscribers found
														</Text>
														<Text
															size="sm"
															c="dimmed"
															ta="center"
															maw={350}
														>
															{searchValue
																? 'No subscribers match your search criteria. Try adjusting your filters.'
																: 'Upload a CSV file to import subscribers.'}
														</Text>
													</Stack>
												</Stack>
											</Table.Td>
										</Table.Tr>
									) : (
										subscribers.map((subscriber) => (
											<Table.Tr key={subscriber.id}>
												<Table.Td>
													<Checkbox
														checked={selectedIds.includes(subscriber.id)}
														onChange={(e) => {
															if (e.currentTarget.checked) {
																setSelectedIds((prev) => [
																	...prev,
																	subscriber.id,
																]);
															} else {
																setSelectedIds((prev) =>
																	prev.filter(
																		(id) => id !== subscriber.id
																	)
																);
															}
														}}
													/>
												</Table.Td>
												<Table.Td>
													<Text>{subscriber.email || '—'}</Text>
												</Table.Td>
												<Table.Td>
													<Text c="dimmed">
														{[subscriber.firstName, subscriber.lastName]
															.filter(Boolean)
															.join(' ') || '—'}
													</Text>
												</Table.Td>
												<Table.Td>
													<Badge
														variant="light"
														color={statusBadgeColor(subscriber.status)}
													>
														{subscriber.status}
													</Badge>
												</Table.Td>
												<Table.Td>
													<Text c="dimmed">
														{subscriber.createdAt
															? formatDateForTable(
																	subscriber.createdAt
																)
															: '—'}
													</Text>
												</Table.Td>
											</Table.Tr>
										))
									)}
								</Table.Tbody>
							</Table>
						</Table.ScrollContainer>
					</Paper>
				</DataContainer>

				{data?.getAllAdminNewsletterSubscriber?.pageInfo && (
					<Group justify="space-between" align="center" mt="md">
						<Text size="sm" c="dimmed">
							Showing{' '}
							{Math.min(
								(currentPage - 1) * pageSize + 1,
								data.getAllAdminNewsletterSubscriber.pageInfo.totalCount
							)}{' '}
							-{' '}
							{Math.min(
								currentPage * pageSize,
								data.getAllAdminNewsletterSubscriber.pageInfo.totalCount
							)}{' '}
							of {data.getAllAdminNewsletterSubscriber.pageInfo.totalCount} items
						</Text>
						<Pagination
							value={currentPage}
							onChange={handlePageChange}
							total={Math.max(1, data.getAllAdminNewsletterSubscriber.pageInfo.totalPages)}
							size="sm"
						/>
					</Group>
				)}

				<SendEmailModal
					opened={sendModalOpen}
					onClose={() => setSendModalOpen(false)}
					profileIds={selectedIds}
					sendToAll={selectAllMatching}
					totalCount={totalCount}
					onSuccess={() => {
						setSelectedIds([]);
						setSelectAllMatching(false);
						refetch();
					}}
				/>

				<Modal
					opened={deleteConfirmOpen}
					onClose={() => setDeleteConfirmOpen(false)}
					title="Delete subscribers"
					size="sm"
					centered
				>
					<Stack gap="md">
						<Text size="sm">
							Are you sure you want to delete {selectedIds.length} subscriber
							{selectedIds.length === 1 ? '' : 's'}? This action cannot be undone.
						</Text>
						<Group justify="flex-end" gap="sm">
							<Button
								variant="default"
								onClick={() => setDeleteConfirmOpen(false)}
								disabled={deleting}
							>
								Cancel
							</Button>
							<Button
								color="red"
								onClick={handleDelete}
								loading={deleting}
								leftSection={<IconTrash size={16} />}
							>
								Delete
							</Button>
						</Group>
					</Stack>
				</Modal>
			</Stack>
		</>
	);
}
